import { useTranslations } from 'next-intl';
import {
  Badge,
  BodyStrong,
  Button,
  IconButton,
  MetaDefault,
  Tooltip,
} from '@terra/ui-design-system';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Cancel01Icon } from '@terra/ui-icons';
import type { WorkspaceSummary } from '../../../electron-shared/workspace/workspace-descriptor';
import {
  findAttackSurface,
  getAttackSurfaceIcon,
} from '../../layout/target-selector/attackSurfaces';

type RecentWorkspaceCardProps = {
  workspace: WorkspaceSummary;
  showTenantName: boolean;
  onOpen: (workspace: WorkspaceSummary) => void;
  onRemove: (workspaceId: string) => void;
};

export function RecentWorkspaceCard({
  workspace,
  showTenantName,
  onOpen,
  onRemove,
}: RecentWorkspaceCardProps) {
  const t = useTranslations('proxy.workspaces');
  const tCommon = useTranslations('common');
  const Icon = getAttackSurfaceIcon(workspace.targetType);
  const surface = findAttackSurface(workspace.targetType);
  const surfaceLabel = surface
    ? t(`targetTypeLabel.${surface.labelKey}`)
    : null;
  const subtitle = [showTenantName ? workspace.tenantName : null, surfaceLabel]
    .filter((part): part is string => Boolean(part))
    .join(' · ');

  return (
    <Box className="group" position="relative" minW="0">
      <Button
        variant="outline"
        w="full"
        h="auto"
        px="4"
        py="3.5"
        borderRadius="lg"
        justifyContent="flex-start"
        textAlign="start"
        aria-label={t('openTarget', { name: workspace.targetName })}
        onClick={() => onOpen(workspace)}
      >
        <HStack gap="2.5" w="full" minW="0" alignItems="flex-start">
          <Box color="fg.neutral.muted" display="flex" flexShrink={0} pt="0.5">
            <Icon size={16} />
          </Box>
          <VStack gap="0.5" flex="1" minW="0" alignItems="flex-start">
            <BodyStrong truncate maxW="full" data-bi-unmask="true">
              {workspace.targetName}
            </BodyStrong>
            {subtitle && (
              <MetaDefault
                truncate
                maxW="full"
                color="fg.neutral.muted"
                data-bi-unmask="true"
              >
                {subtitle}
              </MetaDefault>
            )}
          </VStack>
          {workspace.isOpen && (
            <Badge size="sm" variant="emphasis" flexShrink={0}>
              {t('currentlyOpen')}
            </Badge>
          )}
        </HStack>
      </Button>
      {!workspace.isOpen && (
        <Box
          position="absolute"
          top="2"
          right="2"
          opacity={0}
          pointerEvents="none"
          _groupHover={{ opacity: 1, pointerEvents: 'auto' }}
          _groupFocusWithin={{ opacity: 1, pointerEvents: 'auto' }}
        >
          <Tooltip title={t('removeFromRecent')}>
            <IconButton
              variant="ghost"
              size="2xs"
              aria-label={tCommon('remove')}
              onClick={() => onRemove(workspace.id)}
            >
              <Cancel01Icon size={12} />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
}
